"use client";

import Image from "next/image";
import { useEffect, useMemo, useRef } from "react";

const UNSPLASH = "https://images.unsplash.com";

/** Default placeholders — paired by index. */
const DEFAULT_PAIRS: { before: string; after: string }[] = [
  {
    before: `${UNSPLASH}/photo-1500648767791-00dcc994a43e?auto=format&fit=crop&w=200&h=280&q=80`,
    after: `${UNSPLASH}/photo-1560250097-0b93528c311a?auto=format&fit=crop&w=200&h=280&q=80`,
  },
  {
    before: `${UNSPLASH}/photo-1494790108377-be9c29b29330?auto=format&fit=crop&w=200&h=280&q=80`,
    after: `${UNSPLASH}/photo-1573496359142-b8d87734a5a2?auto=format&fit=crop&w=200&h=280&q=80`,
  },
  {
    before: `${UNSPLASH}/photo-1506794778202-cad84cf45f1d?auto=format&fit=crop&w=200&h=280&q=80`,
    after: `${UNSPLASH}/photo-1519085360753-af0119f7cbe7?auto=format&fit=crop&w=200&h=280&q=80`,
  },
  {
    before: `${UNSPLASH}/photo-1438761681033-6461ffad8d80?auto=format&fit=crop&w=200&h=280&q=80`,
    after: `${UNSPLASH}/photo-1580489944761-15a19d654956?auto=format&fit=crop&w=200&h=280&q=80`,
  },
  {
    before: `${UNSPLASH}/photo-1534528741775-53994a69daeb?auto=format&fit=crop&w=200&h=280&q=80`,
    after: `${UNSPLASH}/photo-1594744803329-e58b31de8bf5?auto=format&fit=crop&w=200&h=280&q=80`,
  },
];

export type BeforeAfterPair = { before: string; after: string };

export type BeforeAfterMarqueeSliderProps = {
  /** Same-length before/after pairs; index i lines up at the center divider. */
  pairs?: BeforeAfterPair[];
  /** Legacy: zipped by index into pairs when `pairs` is omitted. */
  beforeSrcs?: string[];
  afterSrcs?: string[];
  className?: string;
  compact?: boolean;
};

const labelClass =
  "rounded-full bg-black/40 px-2.5 py-1 text-xs font-bold uppercase tracking-wide text-white shadow-sm backdrop-blur-sm sm:px-3 sm:text-sm";

function resolvePairs(
  pairsProp: BeforeAfterPair[] | undefined,
  beforeSrcs: string[] | undefined,
  afterSrcs: string[] | undefined
): BeforeAfterPair[] {
  if (pairsProp?.length) return pairsProp;
  const b = (beforeSrcs ?? []).filter(Boolean);
  const a = (afterSrcs ?? []).filter(Boolean);
  const n = Math.min(b.length, a.length);
  return Array.from({ length: n }, (_, i) => ({ before: b[i], after: a[i] }));
}

function clamp01(value: number): number {
  return Math.max(0, Math.min(1, value));
}

function GeneratedBadge() {
  return (
    <div className="inline-flex items-center gap-1 rounded-full bg-white px-2 py-1 text-[10px] font-semibold leading-none text-neutral-700 shadow-sm ring-1 ring-black/5">
      <span
        className="inline-flex h-3.5 w-3.5 items-center justify-center rounded-full bg-orange-100 text-[9px] text-orange-500"
        aria-hidden
      >
        ✦
      </span>
      <span className="whitespace-nowrap">AI Generated by PFPMaker</span>
    </div>
  );
}

/**
 * Single right-moving marquee.
 * Each tile is "before" and gradually reveals its paired "after" as it approaches/crosses center.
 */
export default function BeforeAfterMarqueeSlider({
  pairs: pairsProp,
  beforeSrcs,
  afterSrcs,
  className = "",
  compact: _compact = false,
}: BeforeAfterMarqueeSliderProps) {
  const pairs = useMemo(
    () => resolvePairs(pairsProp, beforeSrcs, afterSrcs),
    [pairsProp, beforeSrcs, afterSrcs]
  );
  const list = pairs.length > 0 ? pairs : DEFAULT_PAIRS;
  const tiles = useMemo(() => {
    const copy = [...list, ...list, ...list, ...list];
    return copy;
  }, [list]);
  const containerRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    let raf = 0;

    const updateReveal = () => {
      const container = containerRef.current;
      if (!container) {
        raf = requestAnimationFrame(updateReveal);
        return;
      }

      const containerRect = container.getBoundingClientRect();
      const separatorX = containerRect.left + containerRect.width / 2;

      for (const item of itemRefs.current) {
        if (!item) continue;
        const rect = item.getBoundingClientRect();
        // Exact split at the global center separator:
        // left side of split is "before", right side is "after".
        const splitPx = Math.max(0, Math.min(rect.width, separatorX - rect.left));
        const afterRatio = clamp01(1 - splitPx / rect.width);

        item.style.setProperty("--after-split", `${splitPx}px`);
        item.style.setProperty("--after-opacity", String(afterRatio));
      }

      raf = requestAnimationFrame(updateReveal);
    };

    raf = requestAnimationFrame(updateReveal);
    return () => cancelAnimationFrame(raf);
  }, [tiles.length]);

  return (
    <div
      ref={containerRef}
      className={`relative w-full min-w-0 overflow-hidden py-1 ${className}`.trim()}
      role="region"
      aria-label="Before and after examples"
    >
      <div
        className="before-after-track flex w-max items-center gap-2 pr-2 will-change-transform"
        style={{
          maskImage: "linear-gradient(to right, transparent 0%, black 6%, black 94%, transparent 100%)",
          WebkitMaskImage: "linear-gradient(to right, transparent 0%, black 6%, black 94%, transparent 100%)",
        }}
      >
        {tiles.map((pair, i) => (
          <div
            key={`${pair.before}-${pair.after}-${i}`}
            ref={(el) => {
              itemRefs.current[i] = el;
            }}
            className="relative w-[230px] shrink-0 overflow-hidden rounded-2xl border border-base-300 bg-base-300 aspect-[9.6/12]"
            style={{ ["--after-split" as string]: "100%", ["--after-opacity" as string]: 0 }}
          >
            <Image
              src={pair.before}
              alt={`Before example ${(i % list.length) + 1}`}
              fill
              className="object-cover"
              sizes="230px"
              quality={80}
            />

            <div
              className="absolute inset-0 overflow-hidden"
              style={{ clipPath: "inset(0 0 0 var(--after-split))" }}
            >
              <Image
                src={pair.after}
                alt={`After example ${(i % list.length) + 1}`}
                fill
                className="object-cover"
                sizes="230px"
                quality={80}
              />

              <div
                className="absolute inset-x-0 bottom-1 flex items-center justify-center px-1"
                style={{ opacity: "var(--after-opacity)" }}
              >
                <GeneratedBadge />
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="pointer-events-none absolute left-1/2 top-3 z-20 flex -translate-x-1/2 items-center gap-2">
        <span className={labelClass}>Before</span>
        <span className={labelClass}>After</span>
      </div>
      <div
        className="pointer-events-none absolute inset-y-0 left-1/2 z-10 w-0.5 -translate-x-1/2 bg-gradient-to-b from-transparent via-white to-transparent shadow-[0_0_10px_#fff]"
        aria-hidden
      />
      <style jsx>{`
        .before-after-track {
          animation: before-after-marquee-right 36s linear infinite;
        }
        @keyframes before-after-marquee-right {
          from {
            transform: translateX(-25%);
          }
          to {
            transform: translateX(0);
          }
        }
        @media (prefers-reduced-motion: reduce) {
          .before-after-track {
            animation: none;
            transform: translateX(-25%);
          }
        }
      `}</style>
    </div>
  );
}
